import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";
import { AudienceToggle } from "@/components/AudienceToggle";
import { PulseTrace } from "@/components/layout/PulseTrace";
import { ButtonLink, buttonClass } from "@/components/system";
import { FOOTER_COLUMNS, PUBLIC_NAV, isCurrentNavPath } from "@/lib/site-nav";
import "@/styles/brand-a.css";
import "@/styles/directory-redesign.css";
import "@/styles/phase3-loops.css";
import "@/styles/board.css";

/**
 * The one frame for every public page: header, main, footer. Page content
 * still sits inside the legacy wrapper classes until each page is rebuilt on
 * the design system (styles/foundation.css re-points their variables).
 */
export function PublicShell({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const { city } = useCity();
  const { isAuthenticated } = useAuth();
  const { pathname, search } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => setMenuOpen(false), [pathname]);

  const authLinks = isAuthenticated ? (
    <ButtonLink to="/board" size="sm">Board</ButtonLink>
  ) : (
    <>
      <ButtonLink to="/auth" variant="ghost" size="sm">Sign in</ButtonLink>
      <ButtonLink to="/join" size="sm">Join</ButtonLink>
    </>
  );

  return (
    <div className={`ds-site ${className}`.trim()}>
      <Link className="ds-skip" to={`${pathname}${search}#main`}>Skip to content</Link>
      <header className="ds-header">
        <div className="ds-container">
          <div className="ds-header__bar">
            <Link to="/" className="ds-brand" aria-label={`PulseBoard ${city.name} home`}>
              <PulseMark />
              <span>PulseBoard</span>
              <span className="ds-brand__city">{city.name}</span>
            </Link>
            <nav className="ds-nav" aria-label="Primary">
              {PUBLIC_NAV.map((item) => (
                <Link key={item.to} to={item.to} aria-current={isCurrentNavPath(pathname, item.to) ? "page" : undefined}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="ds-header__end">
              <AudienceToggle />
              <div className="ds-header__auth ds-cluster">{authLinks}</div>
              <button
                type="button"
                className={buttonClass({ variant: "ghost", size: "sm", iconOnly: true }, "ds-menu-btn")}
                aria-expanded={menuOpen}
                aria-controls="ds-mobile-nav"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMenuOpen((open) => !open)}
              >
                {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
              </button>
            </div>
          </div>
          <nav id="ds-mobile-nav" className="ds-mobile-nav" data-open={menuOpen} aria-label="Primary">
            {PUBLIC_NAV.map((item) => (
              <Link key={item.to} to={item.to} aria-current={isCurrentNavPath(pathname, item.to) ? "page" : undefined}>
                {item.label}
              </Link>
            ))}
            <div className="ds-mobile-nav__auth">{authLinks}</div>
          </nav>
        </div>
        <PulseTrace />
      </header>

      <main id="main" className="ds-site__main">
        <div className="brand-a pb-dir-page pb-public-shell__content">{children}</div>
      </main>

      <footer className="ds-footer">
        <div className="ds-container">
          <div className="ds-footer__grid">
            <div className="ds-footer__col">
              <Link to="/" className="ds-brand" aria-label={`PulseBoard ${city.name} home`}>
                <PulseMark />
                <span>PulseBoard</span>
              </Link>
              <p className="ds-small">
                Source-checked specialist listings and referral availability for {city.name}.
              </p>
            </div>
            {FOOTER_COLUMNS.map((column) => (
              <nav key={column.heading} className="ds-footer__col" aria-label={column.heading}>
                <p className="ds-eyebrow">{column.heading}</p>
                {column.links.map((link) => (
                  <Link key={link.to} to={link.to}>{link.label}</Link>
                ))}
              </nav>
            ))}
          </div>
          <div className="ds-footer__base">
            <span>© {new Date().getFullYear()} Amida Health Pty Ltd · {city.name} pilot</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function PulseMark() {
  return (
    <svg viewBox="0 0 28 28" aria-hidden="true">
      <rect width="28" height="28" rx="8" fill="#187575" />
      <path
        d="M6 15h4.2l1.7-4.5 3 8.5 1.8-4H22"
        stroke="#FFFFFF"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        fill="none"
      />
    </svg>
  );
}
